import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import useWilmaUsers from "../../../../../data/useWilmaUsers";
import { useAuth } from "../../../../../contexts/AuthContext";
import { ICallHistoryEntry } from "../../../../../models/CallQueue";

function when(iso: string, now: Date) {
  const date = new Date(iso);
  const sameDay = date.toDateString() === now.toDateString();
  return sameDay
    ? date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export default function LeadHistory({ entries, atfName }: { entries: ICallHistoryEntry[]; atfName?: string | null }) {
  const { t } = useTranslation("leads");
  const { userId } = useAuth();
  const { wilmaUsers } = useWilmaUsers();
  const now = new Date();

  const names = useMemo(() => {
    const map = new Map<string, string>();
    (wilmaUsers.data?.data ?? []).forEach((user) => map.set(user.id, user.fullname || user.forename || ""));
    return map;
  }, [wilmaUsers.data]);

  const who = (id?: string | null) => {
    if (!id) return null;
    if (id === userId) return t("you");
    return names.get(id) || null;
  };

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 px-4 py-3">
        <h2 className="text-[15px] font-semibold text-gray-900">{t("History")}</h2>
      </div>
      <ol className="flex flex-col gap-3 px-4 py-3 text-[13px]">
        {entries.map((entry, i) => {
          const person = who(entry.userId);
          return (
            <li key={`${entry.at}-${i}`} className="flex gap-2.5">
              <span className="w-12 flex-none text-xs text-gray-600">{when(entry.at, now)}</span>
              <span className="text-gray-800">
                {entry.text}
                {person ? ` · ${person}` : ""}
              </span>
            </li>
          );
        })}
      </ol>
      {/* Only when it's known: "Unknown ATF" helps nobody. */}
      {atfName && <div className="px-4 pb-3.5 text-xs text-gray-600">{t("ATF")}: {atfName}</div>}
    </section>
  );
}
